import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown, Plus, Search } from 'lucide-react'
import { cn } from './cn'
import { CountBadge } from './count-badge'
import { useFieldA11y } from './field'
import { usePopoverPlacement } from './use-popover-placement'

export interface PickerOption {
  value: string
  label: string
  /** Second line under the label — what the option means, not a tooltip. */
  description?: string
  /** An unavailable option remains visible but cannot be selected. */
  disabled?: boolean
  /** Muted explanation shown beneath an unavailable option. */
  disabledReason?: string
  /** Optional grouping heading rendered above the first option of each group. */
  group?: string
}

/**
 * A dense inline selector uses the same interaction contract at a smaller rhythm.
 * A compact picker sizes to its current label; pass a width in `className` to
 * pin it (a toolbar does, so switching options never moves its neighbours).
 */
export type PickerSize = 'default' | 'compact'

interface PickerBaseProps {
  options: PickerOption[]
  /** Required: a combobox with no name is unusable in a screen reader. */
  label: string
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string
  /** Render the search field when the list is larger than this value. */
  searchThreshold?: number
  searchPlaceholder?: string
  emptyLabel?: string
  /** Replaces the empty list body when the picker has no options. */
  emptyContent?: React.ReactNode
  /** Fixed content rendered below the picker list. */
  footer?: React.ReactNode
  /**
   * Makes the picker creatable: when the search finds nothing by the typed
   * name, the list offers a LAST ROW that creates it.
   *
   * A row rather than a button beside the input, because that is where the
   * person is already looking and it keeps「選一個」and「做一個」on the same
   * keyboard path — Arrow-Down past the last match reaches it. The menu closes
   * as it fires WITHOUT returning focus to the trigger: the caller is expected
   * to open something that takes focus itself (a dialog), and refocusing the
   * trigger would pull focus back out of it.
   */
  createOption?: {
    /** Row label for the typed text, e.g.「新增分類：『語音硬體』」. */
    label: (query: string) => string
    onCreate: (query: string) => void
  }
  disabled?: boolean
  size?: PickerSize
  className?: string
  /** Trigger id. Inside a `Field` with `htmlFor`, that id is used when this is omitted. */
  id?: string
}

export interface PickerSingleProps extends PickerBaseProps {
  multiple?: false
  value: string | null
  onChange: (value: string) => void
}

export interface PickerMultipleProps extends PickerBaseProps {
  multiple: true
  value: string[]
  onChange: (values: string[]) => void
}

export type PickerProps = PickerSingleProps | PickerMultipleProps

const DEFAULT_SEARCH_THRESHOLD = 6
const MENU_MIN_WIDTH = 200

/**
 * 可搜尋下拉，可用於單選或多選。
 *
 * The list uses viewport-fixed positioning so it stays visible when its
 * trigger sits in a scrolling panel. Consumers with a dynamic option set can
 * pass `searchThreshold={0}` to keep the search affordance stable over time.
 */
export function Picker(props: PickerProps) {
  const {
    options,
    label,
    placeholder,
    searchThreshold = DEFAULT_SEARCH_THRESHOLD,
    searchPlaceholder,
    emptyLabel = 'No matching options',
    emptyContent,
    footer,
    createOption,
    disabled,
    size = 'default',
    className,
    id,
  } = props
  // Inside a Field the trigger answers to the Field's `<label htmlFor>` and is
  // described by its hint or error, the same contract Input and Textarea keep.
  const { controlId, invalid, descriptionIds } = useFieldA11y()
  const multiple = props.multiple === true
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()

  const showSearch = searchThreshold === 0 || options.length > searchThreshold
  const needle = query.trim().toLowerCase()
  const visible = needle
    ? options.filter(
        (option) =>
          option.label.toLowerCase().includes(needle) ||
          (option.description ?? '').toLowerCase().includes(needle)
      )
    : options
  const optionId = (option: PickerOption) => `${listboxId}-option-${option.value}`
  const createRowId = `${listboxId}-create`
  const typed = query.trim()
  // Offered only for a name nothing already answers to. Matching on the LABEL
  // rather than the value: what the person typed is a name, and offering to
  // create「AI 工具」while「AI 工具」sits one row above is the picker arguing
  // with itself.
  const showCreate =
    createOption !== undefined &&
    typed !== '' &&
    !options.some((option) => option.label.trim().toLowerCase() === needle)
  const createIndex = showCreate ? visible.length : -1
  const activeOptionId =
    !open
      ? undefined
      : highlight === createIndex && createIndex >= 0
        ? createRowId
        : visible[highlight] && !visible[highlight].disabled
          ? optionId(visible[highlight])
          : undefined
  // An empty string can be a deliberate option (for example an unassigned
  // speaker shown as 「—」), so only null represents no single selection.
  const values = multiple ? props.value : props.value !== null ? [props.value] : []
  const selected = values.length === 1 ? options.find((option) => option.value === values[0]) : null
  const selectionCount = values.length
  // 2+ selections show the FIRST value plus a `+N` badge for the rest — not
  // the field name — so the trigger always answers "which one(s)", the same
  // rule Dropdown uses (dropdown.tsx, docs/map/frontend/shared-ui.md).
  const firstSelectedLabel = options.find((option) => option.value === values[0])?.label ?? ''
  const triggerText =
    selectionCount === 0 ? (placeholder ?? '') : selectionCount === 1 ? (selected?.label ?? '') : firstSelectedLabel

  const [position, resetPosition] = usePopoverPlacement(
    open,
    { trigger: buttonRef, panel: menuRef, scrollArea: scrollAreaRef, content: listboxRef },
    [footer, showSearch, visible.length, showCreate],
    { minWidth: MENU_MIN_WIDTH }
  )

  const close = useCallback(
    (refocus = true) => {
      setOpen(false)
      setQuery('')
      resetPosition()
      if (refocus) buttonRef.current?.focus()
    },
    [resetPosition]
  )

  useEffect(() => {
    if (!open) return
    if (showSearch) searchRef.current?.focus()
    const selectedIndex = visible.findIndex(
      (option) => values.includes(option.value) && !option.disabled
    )
    setHighlight(selectedIndex >= 0 ? selectedIndex : Math.max(0, visible.findIndex((option) => !option.disabled)))
    const onPointerDown = (event: PointerEvent) => {
      if (
        !rootRef.current?.contains(event.target as Node) &&
        !menuRef.current?.contains(event.target as Node)
      ) {
        close(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
    // The initial highlighted option is chosen on open. Re-running on every
    // keystroke would fight keyboard navigation for control of the highlight.
  }, [open, showSearch, close])

  const runCreate = () => {
    if (!createOption || typed === '') return
    // No refocus: the caller opens something that takes focus itself.
    close(false)
    createOption.onCreate(typed)
  }

  const commit = (option: PickerOption) => {
    if (option.disabled) return
    if (props.multiple) {
      const next = props.value.includes(option.value)
        ? props.value.filter((value) => value !== option.value)
        : [...props.value, option.value]
      props.onChange(next)
      return
    }
    props.onChange(option.value)
    close()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
      return
    }
    if (event.key === 'Tab' && open) {
      close(false)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const enabledIndexes = visible.flatMap((option, index) => (option.disabled ? [] : [index]))
      if (createIndex >= 0) enabledIndexes.push(createIndex)
      if (enabledIndexes.length === 0) return
      const delta = event.key === 'ArrowDown' ? 1 : -1
      setHighlight((current) => {
        const currentEnabledIndex = enabledIndexes.indexOf(current)
        const nextEnabledIndex =
          currentEnabledIndex === -1
            ? delta === 1
              ? 0
              : enabledIndexes.length - 1
            : (currentEnabledIndex + delta + enabledIndexes.length) % enabledIndexes.length
        return enabledIndexes[nextEnabledIndex]
      })
      return
    }
    if (event.key === 'Enter' && open) {
      // Enter on a focusable element inside the footer or empty/error content
      // (a retry button, the manage link) must activate THAT element — the
      // select-the-highlight shortcut only owns Enter from the combobox,
      // search input, and options themselves.
      const target = event.target as HTMLElement
      if (target.closest('a, button') && !target.closest('[role="option"]') && !target.closest('[role="combobox"]')) return
      event.preventDefault()
      if (highlight === createIndex && createIndex >= 0) {
        runCreate()
        return
      }
      const option = visible[highlight]
      if (option && !option.disabled) commit(option)
    }
  }

  let lastGroup: string | undefined

  return (
    <div
      className={cn('relative', size === 'compact' ? 'w-fit max-w-full' : 'w-full', className)}
      ref={rootRef}
    >
      <button
        ref={buttonRef}
        id={id ?? controlId}
        type="button"
        className={cn(
          'flex items-center justify-between gap-2 border border-border bg-background text-left text-foreground transition-colors focus:border-foreground focus:outline-2 focus:outline-[var(--fg-soft)] disabled:cursor-not-allowed disabled:opacity-40',
          size === 'compact'
            ? 'h-7 w-full rounded-[var(--radius-sm)] px-2 text-xs'
            : 'h-[var(--ctl-h)] w-full rounded-[var(--radius)] px-3 text-base',
          open && 'border-foreground outline-2 outline-[var(--fg-soft)]'
        )}
        role="combobox"
        aria-label={label}
        aria-describedby={descriptionIds}
        aria-invalid={invalid || undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={activeOptionId}
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKeyDown}
      >
        <span
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 truncate',
            selectionCount === 0 && 'text-muted'
          )}
        >
          <span className="truncate">{triggerText}</span>
          {multiple && selectionCount >= 2 ? <CountBadge count={selectionCount - 1} prefix="+" /> : null}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            size === 'compact' ? 'size-3' : 'size-4',
            'shrink-0 text-muted transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      {open ? (
        <div
          ref={menuRef}
          className="fixed z-[70] flex min-w-[200px] flex-col overflow-hidden rounded-[var(--radius)] border border-border bg-background p-1 shadow-[var(--shadow)]"
          style={
            position
              ? {
                  left: position.left,
                  width: position.width,
                  top: position.top,
                  bottom: position.bottom,
                  maxHeight: position.maxHeight,
                }
              : { visibility: 'hidden' }
          }
          onKeyDown={onKeyDown}
        >
          {showSearch ? (
            <div className="mb-1 flex shrink-0 items-center gap-2 border-b border-border bg-background px-2 py-1.5">
              <Search aria-hidden="true" className="size-4 shrink-0 text-muted" />
              <input
                ref={searchRef}
                type="text"
                className="h-7 min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted"
                value={query}
                aria-label={searchPlaceholder ?? label}
                aria-controls={listboxId}
                aria-activedescendant={activeOptionId}
                placeholder={searchPlaceholder}
                autoComplete="off"
                onChange={(event) => {
                  setQuery(event.target.value)
                  setHighlight(0)
                }}
              />
            </div>
          ) : null}

          <div ref={scrollAreaRef} className="min-h-0 flex-1 overflow-y-auto">
            <div
              ref={listboxRef}
              className="flex flex-col gap-0.5"
              role="listbox"
              id={listboxId}
              aria-label={label}
              aria-multiselectable={multiple || undefined}
            >
              {visible.length === 0 ? (
                options.length === 0 && emptyContent ? (
                  emptyContent
                ) : showCreate ? null : (
                  <p className="px-2 py-3 text-sm text-muted">{emptyLabel}</p>
                )
              ) : (
              visible.map((option, index) => {
                const heading = option.group && option.group !== lastGroup ? option.group : null
                lastGroup = option.group
                const isSelected = values.includes(option.value)
                return (
                  <React.Fragment key={option.value}>
                    {/* A heading has to read as a heading, not as the previous row's
                        description: mono, with a hairline above it. The first row of
                        the list skips the hairline, or it doubles the search field's
                        border. Sticky within the scroll area and opaque, so scrolled
                        into the middle of a long group it still says whose options
                        these are and no row shows through it. */}
                    {heading ? (
                      <p
                        className={cn(
                          'sticky top-0 z-10 bg-[var(--bg)] px-2 pb-1 pt-3 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-muted',
                          index > 0 && 'mt-1.5 border-t border-[var(--line-soft)]'
                        )}
                      >
                        {heading}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      role="option"
                      id={optionId(option)}
                      aria-selected={isSelected}
                      aria-disabled={option.disabled || undefined}
                      disabled={option.disabled}
                      className={cn(
                        'flex w-full items-start gap-2 rounded-[var(--radius)] px-2 py-2 text-left text-sm text-foreground hover:bg-surface focus-visible:bg-surface focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                        index === highlight && 'bg-surface'
                      )}
                      onPointerEnter={() => !option.disabled && setHighlight(index)}
                      onClick={() => commit(option)}
                    >
                      {multiple ? (
                        <span
                          aria-hidden="true"
                          className={cn(
                            'mt-0.5 grid size-4 shrink-0 place-items-center rounded-[var(--radius)] border border-border',
                            isSelected && 'border-foreground bg-foreground text-background'
                          )}
                        >
                          {isSelected ? <Check className="size-3" strokeWidth={2.5} /> : null}
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{option.label}</span>
                        {option.description ? (
                          <span className="mt-0.5 block text-xs text-muted">
                            {option.description}
                          </span>
                        ) : null}
                        {option.disabled && option.disabledReason ? (
                          <span className="mt-0.5 block text-xs text-muted">
                            {option.disabledReason}
                          </span>
                        ) : null}
                      </span>
                      {!multiple && isSelected ? (
                        <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                      ) : null}
                    </button>
                  </React.Fragment>
                )
              })
              )}
              {showCreate && createOption ? (
                <button
                  type="button"
                  role="option"
                  id={createRowId}
                  aria-selected={false}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-[var(--radius)] px-2 py-2 text-left text-sm text-foreground hover:bg-surface focus-visible:bg-surface focus-visible:outline-none',
                    highlight === createIndex && 'bg-surface'
                  )}
                  onPointerEnter={() => setHighlight(createIndex)}
                  onClick={runCreate}
                >
                  <Plus aria-hidden="true" className="size-4 shrink-0 text-muted" />
                  <span className="min-w-0 flex-1 truncate">{createOption.label(typed)}</span>
                </button>
              ) : null}
            </div>
          </div>
          {footer ? <div className="shrink-0 border-t border-border px-2 py-2">{footer}</div> : null}
        </div>
      ) : null}
    </div>
  )
}
